import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Buildings, EnvelopeSimple, Lock, User, ArrowRight, Key } from '@phosphor-icons/react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Etwas ist schief gelaufen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function TenantRegister() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { portalRegister } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    setLoading(true);
    try {
      await portalRegister(email, password, name, inviteCode);
      navigate('/portal');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8 bg-gray-50" data-testid="tenant-register-page">
      <div className="w-full max-w-md bg-white p-6 sm:p-10 border border-gray-200">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Buildings weight="bold" className="w-8 h-8 text-slate-900" />
            <span className="font-heading text-xl font-black tracking-tight">IMMO VERWALTER</span>
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 mb-2">
            Mieter-Portal
          </h1>
          <p className="text-slate-500 text-sm">
            Aktivieren Sie Ihren Zugang mit dem Einladungscode Ihres Vermieters.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Einladungscode</label>
            <div className="relative">
              <Key className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                className="w-full border-b border-slate-300 bg-transparent pl-8 py-3 focus:border-slate-900 outline-none font-mono uppercase"
                placeholder="Code vom Vermieter"
                required
                data-testid="invite-code-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Name</label>
            <div className="relative">
              <User className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border-b border-slate-300 bg-transparent pl-8 py-3 focus:border-slate-900 outline-none"
                placeholder="Max Mustermann"
                required
                data-testid="register-name-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">E-Mail</label>
            <div className="relative">
              <EnvelopeSimple className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-b border-slate-300 bg-transparent pl-8 py-3 focus:border-slate-900 outline-none"
                placeholder="ihre@email.de"
                required
                data-testid="register-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Passwort</label>
            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-b border-slate-300 bg-transparent pl-8 py-3 focus:border-slate-900 outline-none"
                placeholder="Mind. 6 Zeichen"
                required
                minLength={6}
                data-testid="register-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white py-3 px-6 font-medium hover:bg-slate-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="register-submit-button"
          >
            {loading ? 'Wird registriert...' : 'Zugang aktivieren'}
            <ArrowRight weight="bold" className="w-4 h-4" />
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          Bereits registriert?{' '}
          <Link to="/login" className="text-slate-900 font-medium hover:underline">
            Anmelden
          </Link>
        </p>
      </div>
    </div>
  );
}
